import isEqual from 'lodash/isEqual';
import React, { useState, useEffect, useContext, useRef } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { FormControl, FormGroup } from 'react-bootstrap';
import { FormContext, skipUpdate } from '../FormContext';
import FormInputLabel from './parts/FormInputLabel';
import FormInputHelp from './parts/FormInputHelp';
import '../styles/textarea.scss';

const FormInputTextareaItem = React.memo(props => {
  const { controlId, field, noLabel, disabled, originalValue, touched, style } = props;

  const firstUpdate = useRef(true);

  const [value, setValue] = useState(props.value);
  const [valid, setValid] = useState(props.valid);
  const [errorMessage, setErrorMessage] = useState(props.errorMessage);
  const [dirty, setDirty] = useState(!isEqual(props.value, originalValue));

  const { ee, trackDirty } = useContext(FormContext);

  const onBlur = () => {
    const result = props.onBlur(field.name);

    if (result) {
      setValue(result.displayValue !== undefined ? result.displayValue : value);
      setValid(result.valid !== undefined ? result.valid : valid);
      setErrorMessage(result.validError !== undefined ? result.validError : errorMessage);
      setDirty(!isEqual(originalValue, value));
      return true;
    }

    return false;
  };

  const consume = ({ action, payload: data }) => {
    if (action !== 'update') {
      return;
    }

    let newValue = data.value;

    if (newValue !== undefined) {
      if (Array.isArray(newValue)) {
        newValue = newValue.join('\n');
      }

      setValue(newValue);
      onBlur();
    }
  };

  // Original Value changed from props (usually a submit)
  useEffect(() => {
    if (isEqual(originalValue, value)) {
      setDirty(false);
    } else {
      setDirty(true);
    }
  }, [originalValue]);

  // Validation state changed from props (usually a submit)
  useEffect(() => {
    if (props.valid !== valid) {
      setValid(props.valid);
      setErrorMessage(props.errorMessage);
    }
  }, [props.valid]);

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }

    // TODO Remove stupid array crap at this level
    const dirty = !isEqual(Array.isArray(originalValue) ? originalValue.join('\n') : originalValue, value);

    setDirty(dirty);
    props.onChange(field.name, value);
  }, [value]);

  useEffect(() => {
    ee.on(controlId, consume);

    return () => {
      ee.off(controlId);
    };
  }, []);

  const isDirty = dirty && valid && !field.readOnly && trackDirty;
  const isInvalid = !valid && touched && !field.readOnly;
  const isReadOnly = field.readOnly || disabled;

  const containerClass = classNames('form-textarea', {
    'is-invalid': isInvalid,
    'is-enabled': !isReadOnly,
    'is-dirty': isDirty,
    'is-readonly': isReadOnly,
    [style]: !!style,
    'is-empty': value === '',
  });

  // Default field type is text, also not using html5 date fields
  const type = field.type && field.type !== 'date' ? field.type : 'text';

  const rows = field.visibleLines || 4;

  return (
    <FormGroup controlId={controlId} className={containerClass} key={controlId} id={`${controlId}-control`}>
      <FormInputLabel hide={noLabel}>{field.label}</FormInputLabel>
      <FormControl
        as="textarea"
        type={type}
        required={field.required}
        minLength={field.minLen}
        pattern={field.pattern}
        min={field.min}
        readOnly={isReadOnly}
        value={value}
        onChange={e => setValue(e.target.value)}
        onBlur={onBlur}
        disabled={isReadOnly}
        placeholder={field.placeholder}
        rows={rows}
      />
      <FormInputHelp errorText={errorMessage}>{field.helpText}</FormInputHelp>
    </FormGroup>
  );
}, skipUpdate);

FormInputTextareaItem.defaultProps = {
  disabled: false,
  errorMessage: '',
  field: {},
  noLabel: false,
  onBlur: () => {},
  onChange: () => {},
  originalValue: '',
  style: '',
  touched: false,
  valid: true,
  value: '',
};

FormInputTextareaItem.propTypes = {
  controlId: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  errorMessage: PropTypes.string,
  field: PropTypes.object,
  noLabel: PropTypes.bool,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  originalValue: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  style: PropTypes.string,
  touched: PropTypes.bool,
  valid: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
};

export default FormInputTextareaItem;
